/**
 * Web Push — register service worker, request permission, subscribe, send to backend.
 */

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function fetchVapidPublicKey() {
  const res = await fetch('/api/push/vapid-public-key');
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `VAPID key fetch failed: ${res.status}`);
  }
  const data = await res.json();
  return data.publicKey;
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers not supported');
  }
  const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;
  return reg;
}

export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    throw new Error('Notifications not supported');
  }
  const perm = await Notification.requestPermission();
  return perm;
}

export async function subscribeToPush(registration, vapidPublicKey) {
  if (!registration.pushManager) {
    throw new Error('Push not supported');
  }
  const key = urlBase64ToUint8Array(vapidPublicKey);
  const sub = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key,
  });
  return sub.toJSON ? sub.toJSON() : sub;
}

/**
 * Full flow: permission → subscribe → POST subscription (requires auth token).
 */
export async function enableStreakPushReminders(idToken, timezone) {
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    return { ok: false, permission, error: 'Permission not granted' };
  }

  const registration = await registerServiceWorker();
  const publicKey = await fetchVapidPublicKey();
  const subscription = await subscribeToPush(registration, publicKey);

  const res = await fetch('/api/creator-streak/push-subscribe', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      subscription,
      timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      permission: 'granted',
    }),
  });

  const data = res.ok ? await res.json() : await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, error: data.error || res.status };
  }
  return { ok: true, ...data };
}

export function getNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export async function getExistingSubscription() {
  try {
    const reg = await navigator.serviceWorker.ready;
    return reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}
